/**
 * publicacionesBulk — cliente API para las acciones bulk del BulkActionBar
 * en Publicaciones (extended mode).
 *
 * Endpoints (en construcción por subagente backend paralelo):
 *  - POST /api/clients/:id/catalog/bulk-hide
 *  - POST /api/clients/:id/catalog/bulk-mark-dubious
 *  - POST /api/clients/:id/catalog/bulk-approve
 *  - POST /api/clients/:id/catalog/bulk-edit
 *  - POST /api/clients/:id/catalog/bulk-recataloga  (TODO — todavía no
 *    confirmado en el contrato del subagente backend; ver TODO abajo).
 *
 * Si el backend no está listo, los 404/501 se interpretan como "backend
 * pendiente" y la UI muestra un toast informativo (helper isBackendUnavailable).
 */
import api from '../utils/api';

export interface BulkResult {
  ok: boolean;
  processed?: number;
  errors?: string[];
}

export interface BulkEditFields {
  catalog_category?: string | null;
  subcategory?: string | null;
  segmento?: string | null;
  udm?: string | null;
}

function clientHeaders(clientId: string) {
  return { headers: { 'x-client-id': clientId } };
}

function normalizeResult(data: unknown): BulkResult {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  return {
    ok: typeof d.ok === 'boolean' ? d.ok : true,
    processed: typeof d.processed === 'number' ? d.processed : undefined,
    errors: Array.isArray(d.errors) ? (d.errors as string[]) : undefined,
  };
}

/**
 * Bulk hide — oculta N items del catálogo cliente (afecta tomas pasadas y
 * futuras).
 */
export async function bulkHide(
  clientId: string,
  ids: string[],
  comment?: string
): Promise<BulkResult> {
  // reason: backend whitelist = { out_of_scope, low_confidence, manual, duplicate }
  // 'manual' = decisión humana desde la UI.
  const res = await api.post(
    `/api/clients/${clientId}/catalog/bulk-hide`,
    { ids, reason: 'manual', comment },
    clientHeaders(clientId)
  );
  return normalizeResult(res.data);
}

/**
 * Bulk mark dubious — flagea N items como "dudosos" para revisión.
 */
export async function bulkMarkDubious(
  clientId: string,
  ids: string[],
  comment?: string
): Promise<BulkResult> {
  const res = await api.post(
    `/api/clients/${clientId}/catalog/bulk-mark-dubious`,
    { ids, comment },
    clientHeaders(clientId)
  );
  return normalizeResult(res.data);
}

/**
 * Bulk approve — marca N items como aprobados manualmente. Sale del
 * pipeline de revisión.
 */
export async function bulkApprove(
  clientId: string,
  ids: string[],
  comment?: string
): Promise<BulkResult> {
  const res = await api.post(
    `/api/clients/${clientId}/catalog/bulk-approve`,
    { ids, comment },
    clientHeaders(clientId)
  );
  return normalizeResult(res.data);
}

/**
 * Bulk edit — aplica los campos del form a los N items seleccionados.
 * Cada campo presente se sobreescribe y entra a manually_edited_fields.
 */
export async function bulkEdit(
  clientId: string,
  ids: string[],
  fields: BulkEditFields,
  comment?: string
): Promise<BulkResult> {
  const res = await api.post(
    `/api/clients/${clientId}/catalog/bulk-edit`,
    { ids, fields, comment },
    clientHeaders(clientId)
  );
  return normalizeResult(res.data);
}

/**
 * Bulk re-cataloga — encola un run LLM sobre los N items.
 *
 * TODO(backend): contrato no confirmado todavía con el subagente backend.
 * Provisorialmente reusamos el endpoint del Data Health (triggerRecatalog en
 * src/services/dataHealth.ts) que ya existe y acepta `mode: 'force'` + `ids`.
 * Cuando el subagente backend defina `/catalog/bulk-recataloga` reemplazar.
 */
export async function bulkRecataloga(
  clientId: string,
  ids: string[],
  _comment?: string
): Promise<BulkResult & { runId?: string }> {
  // Mock fallback: reusa endpoint legacy run-batch hasta que backend confirme.
  const res = await api.post(
    `/api/clients/${clientId}/catalog/run-batch`,
    { mode: 'force', idCatalogos: ids },
    clientHeaders(clientId)
  );
  const d = (res.data || {}) as Record<string, unknown>;
  return {
    ok: true,
    processed: ids.length,
    runId: typeof d.runId === 'string' ? d.runId : (typeof d.run_id === 'string' ? d.run_id : undefined),
  };
}

// ----------------------------------------------------------------------------
// Helpers de error
// ----------------------------------------------------------------------------

export function isBackendUnavailable(err: unknown): boolean {
  const e = err as { response?: { status?: number }; code?: string };
  if (!e) return false;
  if (e.response?.status === 404) return true;
  if (e.response?.status === 501) return true;
  if (e.code === 'ECONNREFUSED' || e.code === 'ERR_NETWORK') return true;
  return false;
}

export function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return (
    e?.response?.data?.error ||
    e?.response?.data?.message ||
    e?.message ||
    'Error desconocido'
  );
}
